import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { MonitoringKeywordGroupModel } from '../../models/monitoring-keyword-group.model';
import { SearchContextModel } from '../../models/search-context.model';
import { SearchContextsEnum } from '../../enums/search-contexts.enum';
import { operatorsEnum } from '../../enums/operators.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

export const monitoringKeywordGroupComponentText = {
    keywordGroupName: 'Keyword group {{id}}',
    text: 'text',
    images: 'images',
    headline: 'Headline',
    lead: 'Lead',
    body: 'Body',
    deleteKeywordGroup: 'Delete Keyword Group',
    addKeywordGroup: 'Add Keyword Group',
};

@Component({
    selector: 'agpr-monitoring-keyword-group',
    templateUrl: './monitoring-keyword-group.component.html',
    styleUrls: ['./monitoring-keyword-group.component.scss'],
    providers: [
        getProvider(MonitoringKeywordGroupComponent)
    ],
    animations: [
        trigger('containerState', [
            state('hidden', style({
                height: '0',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class MonitoringKeywordGroupComponent extends ControlValueAccessorModel<MonitoringKeywordGroupModel> {

    @Input() id: string = null;
    @Input() ariaDescribedBy: string;
    @Input() showDelete: boolean = false;

    // Position of this component's keywordGroup in Monitoring Search's array of keywordGroups
    @Input() index: number;

    @Output() valueChange: EventEmitter<MonitoringKeywordGroupModel> = new EventEmitter();
    @Output() deleteClicked: EventEmitter<null> = new EventEmitter<null>();

    value: MonitoringKeywordGroupModel;
    keywords: MonitoringKeywordGroupModel;

    searchContext: SearchContextsEnum = SearchContextsEnum.text;

    header: SearchContextModel = new SearchContextModel(SearchContextsEnum.header);
    lead: SearchContextModel = new SearchContextModel(SearchContextsEnum.lead);
    body: SearchContextModel = new SearchContextModel(SearchContextsEnum.body);
    images: SearchContextModel = new SearchContextModel(SearchContextsEnum.images);

    textSearchContexts: SearchContextModel[] = [];

    containerExpanded: boolean = true;
    relatedConceptsExpanded: boolean = false;
    disableExpandToggles: boolean = true;
    lastVisibleChipAnd: number = -1;
    lastVisibleChipOr: number = -1;
    lastVisibleChipNot: number = -1;

    readonly buttonTypes = ButtonTypesEnum;
    readonly operatorEnum = operatorsEnum;
    readonly searchContexts = SearchContextsEnum;

    constructor() {
        super();
    }

    writeValue(value: MonitoringKeywordGroupModel) {
        this.value = value;
        this.keywords = value.clone();
        this.handleChanges();
        this.onModelChange(this.value);
        this.checkExpandToggleState();
    }

    processSearchContextsChange(event) {
        this.value.searchContexts = event.valueOf();
        this.writeValue(this.value);
    }

    private handleChanges(): void {
        if (this.value) {
            const isImageSearch = this.includesSearchContext(this.images);
            const isHeader = this.includesSearchContext(this.header);
            const isLead = this.includesSearchContext(this.lead);
            const isBody = this.includesSearchContext(this.body);
            if (isImageSearch) {
                this.searchContext = SearchContextsEnum.images;
            } else {
                this.searchContext = SearchContextsEnum.text;
            }
            if (isHeader && !this.textSearchContexts.includes(this.header)) {
                this.textSearchContexts.push(this.header);
            }
            if (isLead && !this.textSearchContexts.includes(this.lead)) {
                this.textSearchContexts.push(this.lead);
            }
            if (isBody && !this.textSearchContexts.includes(this.body)) {
                this.textSearchContexts.push(this.body);
            }
        }
        this.checkExpandToggleState();
    }

    private checkExpandToggleState() {
        this.disableExpandToggles = !this.value || !this.value.hasAnyKeywords();
        if (this.disableExpandToggles && !this.containerExpanded) {
            this.containerExpanded = true;
        }
    }

    private includesSearchContext(searchContext: SearchContextModel): boolean {
        return !!this.value.searchContexts.find(
            currentSearchContext => searchContext.value === currentSearchContext.value
        );
    }

    addConceptKeywords(keywords: MonitoringKeywordGroupModel) {
        this.value.and = this.value.and.concat(keywords.and);
        this.value.or = this.value.or.concat(keywords.or);
        this.value.not = this.value.not.concat(keywords.not);

        this.writeValue(this.value);
    }

    clickDelete(): void {
        this.deleteClicked.emit();
    }

    areChipsOverflowingChange(index: number, operator: operatorsEnum) {
        switch(operator) {
            case operatorsEnum.and:
                this.lastVisibleChipAnd = index;
                break;
            case operatorsEnum.or:
                this.lastVisibleChipOr = index;
                break;
            case operatorsEnum.not:
                this.lastVisibleChipNot = index;
                break;
        }
    }

    removeKeywordChip(operator: operatorsEnum, index: number) {
        switch(operator) {
            case operatorsEnum.and:
                this.value.and = this.value.and.filter((value, i) => i !== index);
                break;
            case operatorsEnum.or:
                this.value.or = this.value.or.filter((value, i) => i !== index);
                break;
            case operatorsEnum.not:
                this.value.not = this.value.not.filter((value, i) => i !== index);
                break;
        }
        this.writeValue(this.value);
    }
}
